<script>
import { shuffle } from "lodash-es";
import { computed, defineComponent, onMounted, reactive, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";

import { AnswerStateEnum, QuizStateEnum } from "@/common/composables/use-vocab-quiz";
import { mapLanguageSpecificTexts, sleep } from "@/common/utils";
import LottieAnimation from "@/components/LottieAnimation.vue";
import QuizSelectorButton from "@/components/vocabularyTrainer/QuizSelectorButton.vue";
import { useSettingsStore } from "@/stores";

import { useGameComponent } from "./common";

const { commonProps, commonEmits } = useGameComponent();

export default defineComponent({
  components: { LottieAnimation, QuizSelectorButton },
  props: commonProps,
  emits: commonEmits,
  /** @param {import("./common").Props} props */
  setup(props, { emit }) {
    const { t } = useI18n();
    const settingsStore = useSettingsStore();

    const { promptId, questions, quizState, userAnswers } = toRefs(props);

    const answerPair = reactive({
      question: undefined,
      answer: undefined
    });

    const questionOptions = ref([]);
    const answerOptions = ref([]);

    const answeredQuestionIds = computed(() => userAnswers.value.map((a) => a.questionId));
    const hasAnsweredAllQuestions = computed(
      () =>
        questionOptions.value.filter((option) => option.state !== AnswerStateEnum.Hidden).length ===
        0
    );

    const isDisabled = computed(() => quizState.value === QuizStateEnum.Answered);

    const targetLanguage = computed(() =>
      settingsStore.activeLanguageGroup === "english" ? "en" : "de"
    );

    const languageSpecificTexts = computed(() =>
      mapLanguageSpecificTexts({
        targetLanguage: {
          english: t("vocab.games.english.matchingPairsPairLemma"),
          german: t("vocab.games.german.matchingPairsPairLemma")
        }
      })
    );

    const scheduleAnswerCleanup = async () => {
      await sleep(800);

      // Trigger button change if wrong answer
      if (answerPair.question.state === AnswerStateEnum.Correct) {
        answerPair.question.state = AnswerStateEnum.Hidden;
        answerPair.answer.state = AnswerStateEnum.Hidden;
      } else {
        answerPair.question.state = AnswerStateEnum.Initial;
        answerPair.answer.state = AnswerStateEnum.Initial;
      }

      // Remove them as answer values
      answerPair.question = undefined;
      answerPair.answer = undefined;

      // Emit when last pair is answered correctly.
      if (hasAnsweredAllQuestions.value) {
        emit("answer");
      } else {
        emit("enableQuiz");
      }
    };

    const checkAnswer = async (option) => {
      // Defer checking answer, if only 1 option is chosen.
      if (!answerPair.question || !answerPair.answer) {
        option.state = AnswerStateEnum.Pending;

        return;
      }

      const resultState = await props.answerValidator(
        answerPair.question.questionId,
        answerPair.answer.value
      );

      answerPair.question.state = resultState;
      answerPair.answer.state = resultState;

      scheduleAnswerCleanup();
    };

    const onLemmaClick = async (option) => {
      // Remember to reset old option.
      if (answerPair.question !== undefined) {
        answerPair.question.state = AnswerStateEnum.Initial;
      }

      // Ability to remove selection again
      if (answerPair.question === option) {
        answerPair.question = undefined;
        return;
      }

      // Set new option and check for answer.
      answerPair.question = option;
      await checkAnswer(option);
    };

    const onTranslationClick = async (option) => {
      // Remember to reset old option.
      if (answerPair.answer !== undefined) {
        answerPair.answer.state = AnswerStateEnum.Initial;
      }

      // Ability to remove selection again
      if (answerPair.answer === option) {
        answerPair.answer = undefined;
        return;
      }

      // Set new option and check for answer.
      answerPair.answer = option;
      await checkAnswer(option);
    };

    onMounted(() => {
      // Question values will always be the options 1st index, answers is the 2nd index.
      questionOptions.value = questions.value.map((question) => ({
        questionId: question.id,
        value: question.options[0],
        text: question.lemmaWordclass
          ? `${question.options[0]} (${question.lemmaWordclass})`
          : question.options[0],
        state: answeredQuestionIds.value.includes(question.id)
          ? AnswerStateEnum.Hidden
          : AnswerStateEnum.Initial
      }));
      // Randomize answer options so Q&A isn't always opposite of each other.
      answerOptions.value = shuffle(
        questions.value.map((question) => ({
          value: question.options[1],
          text: question.translationWordclass
            ? `${question.options[1]} (${question.translationWordclass})`
            : question.options[1],
          state: answeredQuestionIds.value.includes(question.id)
            ? AnswerStateEnum.Hidden
            : AnswerStateEnum.Initial
        }))
      );
    });

    return {
      t,

      questionOptions,
      answerOptions,
      promptId,
      targetLanguage,
      languageSpecificTexts,
      onLemmaClick,
      onTranslationClick,
      isDisabled,
      hasAnsweredAllQuestions
    };
  }
});
</script>

<template>
  <div class="options">
    <div v-if="!hasAnsweredAllQuestions" class="col-12 col-md-8 offset-md-2 options__grid">
      <div class="options__container options__container--lemma">
        <QuizSelectorButton
          v-for="(option, index) in questionOptions"
          :key="index"
          :state="option.state"
          :disabled="isDisabled"
          :aria-labelledby="`question-text-${index} ${promptId}`"
          :text-id="`question-text-${index}`"
          :text="option.text"
          :lang="targetLanguage"
          class="options__question"
          @click="onLemmaClick(option)"
        >
          <span class="sr-only" lang="da">{{ languageSpecificTexts.targetLanguage }}</span>
        </QuizSelectorButton>
      </div>
      <div class="options__container options__container--translation">
        <QuizSelectorButton
          v-for="(option, index) in answerOptions"
          :key="index"
          :state="option.state"
          :disabled="isDisabled"
          :aria-labelledby="`answer-text-${index} ${promptId}`"
          :text-id="`answer-text-${index}`"
          :text="option.text"
          class="options__answer"
          lang="da"
          @click="onTranslationClick(option)"
        >
          <span class="sr-only" lang="da">{{ t("vocab.games.matchingPairsPairTranslation") }}</span>
        </QuizSelectorButton>
      </div>
    </div>
    <div v-else class="col-12 d-flex align-items-center justify-content-center">
      <LottieAnimation class="app-loading__animation" path="loading" />
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.gap-mixin {
  gap: 15px;

  #media-breakpoint-up(lg, {
      gap: 20px;
    });

  #media-breakpoint-up(xxl, {
      gap: 30px;
    });
}

.options {
  height: 100%;

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    .gap-mixin();
  }

  &__container {
    display: grid;
    grid-auto-rows: 1fr;

    .gap-mixin();

    &--lemma {
      grid-column: 1;
    }

    &--translation {
      grid-column: 2;
    }
  }
}
</style>
